/**
 * Aba "Histórico & CRM" do Cliente 360. Espelha o mockup:
 *   ESQUERDA: caixa de registro · Linha do tempo de interações (atendimentos +
 *     eventos herdados do lead, unificados) · card "Lead de origem".
 *   DIREITA: Notas internas · Diagnóstico SPIN · Resumo de contato.
 *
 * Zero perda de dado: timeline, SPIN e orçamentos do lead aparecem aqui/no
 * Pedidos; tudo o mais (tarefas, compromissos, anexos) continua preservado e
 * a um clique em "Abrir lead". Dados via historico-crm + atendimentos.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiActivity,
  FiAlertCircle,
  FiCheck,
  FiClock,
  FiExternalLink,
  FiFileText,
  FiGitMerge,
  FiInbox,
  FiMail,
  FiMessageCircle,
  FiPlus,
  FiTrash2,
  FiUsers,
} from "react-icons/fi";

import { obterHistoricoCrmCliente } from "../../../../services/comercial";
import { formatarDataLocal } from "../../../../utils/dinheiro";
import {
  Badge,
  fmtDataHora,
  fmtDataLonga,
  Panel,
  prefixoHa,
  whatsappUrl,
} from "../cli360";

// `origem` do item da timeline do lead (crm.services.timeline_lead).
const ORIGEM_ATENDIMENTO = "atendimento";

const SPIN_CATS = [
  { key: "SITUACAO", label: "Situação" },
  { key: "PROBLEMA", label: "Problema" },
  { key: "IMPLICACAO", label: "Implicação" },
  { key: "NECESSIDADE", label: "Necessidade" },
];

function tomToTl(tom) {
  if (tom === "success") return "ok";
  if (tom === "warning" || tom === "danger") return "warn";
  if (tom === "neutral") return "muted";
  return "";
}

function tlIcon(tone) {
  if (tone === "ok") return <FiCheck aria-hidden="true" />;
  if (tone === "warn") return <FiAlertCircle aria-hidden="true" />;
  if (tone === "muted") return <FiClock aria-hidden="true" />;
  return <FiActivity aria-hidden="true" />;
}

function NotaItem({ nota, podeExcluir, onExcluir }) {
  const quando = nota.rotulo ? prefixoHa(nota.rotulo) : fmtDataLonga(nota.created_at);
  return (
    <div className="note">
      <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{nota.texto}</div>
      <div className="flex between center" style={{ marginTop: 7 }}>
        <span className="tiny muted">
          {(nota.autor_nome || "—") + (quando ? ` · ${quando}` : "")}
        </span>
        {podeExcluir ? (
          <button
            type="button"
            onClick={onExcluir}
            title="Excluir nota"
            aria-label="Excluir nota"
            style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--ink-4)", display: "grid", placeItems: "center", padding: 2 }}
          >
            <FiTrash2 size={13} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function HistoricoTab({
  request,
  clienteId,
  cliente,
  atendimentos = [],
  notas = [],
  podeEditar,
  navigate,
  onNovaNota,
  onExcluirNota,
}) {
  const [leads, setLeads] = useState([]);
  const [verTudo, setVerTudo] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const data = await obterHistoricoCrmCliente(request, clienteId, { silentError: true });
      setLeads(Array.isArray(data?.leads) ? data.leads : []);
    } catch {
      setLeads([]);
    }
  }, [request, clienteId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Timeline unificada: atendimentos do cliente + eventos herdados do lead.
  //
  // CRM-42: os atendimentos do lead vêm NAS DUAS fontes — o cockpit do
  // cliente já os traz (join `Atendimento.lead -> Lead.cliente`) e a timeline
  // do lead também. Somar as listas fazia três ligações virarem seis e o
  // contador dobrar. O par (origem, id) é o endereço do registro: o item do
  // lead que já veio do cockpit sai, e o que só existe nele — `descricao` e
  // `comentarios` — vai junto para o item que fica.
  const eventos = useMemo(() => {
    const itensDoLead = leads.flatMap((lead) =>
      (lead.timeline || []).map((t, i) => ({
        key: `l${lead.id}_${t.origem || "item"}_${t.id || i}`,
        id: t.id ? String(t.id) : null,
        origem: t.origem || "",
        title: t.titulo,
        text: t.descricao,
        date: t.timestamp,
        who: t.ator_nome,
        canal: null,
        tone: "",
        rotulo: "",
        comentarios: t.comentarios || [],
      }))
    );
    const doLeadPorAtendimento = new Map(
      itensDoLead
        .filter((item) => item.origem === ORIGEM_ATENDIMENTO && item.id)
        .map((item) => [item.id, item])
    );
    const doCockpit = atendimentos.map((x) => {
      const doLead = doLeadPorAtendimento.get(String(x.id));
      return {
        key: `a${x.id}`,
        title: x.assunto || x.tipo_display || "Contato",
        // O que a pessoa escreveu. Backend antigo não manda `descricao` no
        // cockpit: nesse caso ela vem do item do lead, que é o descartado.
        text: x.descricao || doLead?.text || "",
        // `resultado` é o código interno ("SEM_RETORNO"); o rótulo é o
        // display — e é RÓTULO, não corpo: no lugar do texto, ele apagava
        // da ficha o que foi registrado na conversa.
        rotulo: x.resultado_display || "",
        date: x.data,
        who: x.responsavel_nome,
        canal: x.canal_display,
        tone: tomToTl(x.tom),
        comentarios: doLead?.comentarios || [],
      };
    });
    const idsNoCockpit = new Set(atendimentos.map((x) => String(x.id)));
    const semRepetidos = itensDoLead.filter(
      (item) => !(item.origem === ORIGEM_ATENDIMENTO && item.id && idsNoCockpit.has(item.id))
    );
    return [...doCockpit, ...semRepetidos]
      .filter((e) => e.date)
      .sort((x, y) => new Date(y.date) - new Date(x.date));
  }, [atendimentos, leads]);

  const eventosVisiveis = verTudo ? eventos : eventos.slice(0, 12);

  const resumo = useMemo(() => {
    const c = { whatsapp: 0, email: 0, ligacao: 0, reuniao: 0 };
    atendimentos.forEach((x) => {
      const s = `${x.canal || ""} ${x.canal_display || ""} ${x.tipo || ""} ${x.tipo_display || ""}`.toLowerCase();
      if (s.includes("whats")) c.whatsapp += 1;
      else if (s.includes("mail")) c.email += 1;
      else if (s.includes("liga") || s.includes("tele") || s.includes("call")) c.ligacao += 1;
      else if (s.includes("reuni") || s.includes("visit")) c.reuniao += 1;
    });
    return c;
  }, [atendimentos]);

  const spinLead = leads.find((l) => l.spin && SPIN_CATS.some((c) => (l.spin[c.key] || []).length));
  const waUrl = whatsappUrl(cliente?.telefone);

  return (
    <section className="tab-panel">
      <div className="col-main">
        {/* ESQUERDA */}
        <div className="stack" style={{ gap: 16 }}>
          {podeEditar ? (
            <div className="panel">
              <div className="panel-body">
                <button
                  type="button"
                  className="compose-box"
                  style={{ width: "100%", textAlign: "left", background: "transparent", cursor: "text" }}
                  onClick={onNovaNota}
                >
                  Registrar uma nota interna sobre o cliente…
                </button>
                <div className="flex wrap" style={{ gap: 6, marginTop: 11 }}>
                  {waUrl ? (
                    <a className="btn btn-secondary btn-sm" href={waUrl} target="_blank" rel="noreferrer">
                      <FiMessageCircle size={13} aria-hidden="true" /> WhatsApp
                    </a>
                  ) : null}
                  {cliente?.email ? (
                    <a className="btn btn-secondary btn-sm" href={`mailto:${cliente.email}`}>
                      <FiMail size={13} aria-hidden="true" /> E-mail
                    </a>
                  ) : null}
                  <button type="button" className="btn btn-primary btn-sm" onClick={onNovaNota}>
                    <FiPlus size={13} aria-hidden="true" /> Nota
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <Panel
            title="Linha do tempo de interações"
            icon={<FiActivity aria-hidden="true" />}
            action={<span className="tiny muted">{eventos.length} eventos</span>}
          >
            <div className="panel-body">
              {eventosVisiveis.length === 0 ? (
                <div className="muted sm">Nenhuma interação registrada ainda.</div>
              ) : (
                <>
                  <div className="tl" role="list" aria-label="Linha do tempo de interações">
                    {eventosVisiveis.map((e) => (
                      <div className="tl-item" key={e.key} role="listitem">
                        <div className={`tl-dot${e.tone ? ` ${e.tone}` : ""}`}>{tlIcon(e.tone)}</div>
                        <div className="tl-title">{e.title}</div>
                        {e.rotulo ? <div className="tiny muted">{e.rotulo}</div> : null}
                        {e.text ? <div className="tl-text">{e.text}</div> : null}
                        <div className="tl-time">
                          {fmtDataHora(e.date)}
                          {e.who ? ` · ${e.who}` : ""}
                          {e.canal ? ` · ${e.canal}` : ""}
                        </div>
                        {/* Observação presa ao registro (ComentarioTimeline):
                            consultada pelo cliente, ela sumia. */}
                        {(e.comentarios || []).map((c) => (
                          <div className="note" key={c.id} style={{ marginTop: 6, padding: "7px 9px" }}>
                            <div style={{ fontSize: 12.5, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                              {c.texto}
                            </div>
                            <div className="tiny muted" style={{ marginTop: 4 }}>
                              {c.autor_nome || "—"}
                              {c.created_at ? ` · ${formatarDataLocal(c.created_at)}` : ""}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  {eventos.length > 12 ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{ marginTop: 6 }}
                      onClick={() => setVerTudo((v) => !v)}
                    >
                      {verTudo ? "Ver menos" : `Carregar mais (${eventos.length - 12})`}
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </Panel>

          {leads.length > 0 ? (
            <Panel
              title="Lead de origem"
              icon={<FiGitMerge aria-hidden="true" />}
              action={<span className="tiny muted">preservado do CRM</span>}
            >
              <div className="panel-body stack" style={{ gap: 12 }}>
                {leads.map((lead) => (
                  <div className="note" key={lead.id} style={{ background: "var(--surface)" }}>
                    <div className="flex between center wrap" style={{ gap: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{lead.titulo}</div>
                        <div className="flex center wrap" style={{ gap: 8, marginTop: 5 }}>
                          <span className="cmono tiny muted">{lead.numero}</span>
                          <Badge tone="accent" style={{ fontSize: 10 }}>
                            Convertido
                          </Badge>
                          {lead.ganho_em ? (
                            <Badge tone="success" style={{ fontSize: 10 }}>
                              Ganho em {fmtDataLonga(lead.ganho_em)}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() =>
                          navigate(`/crm/leads/${lead.id}`, { state: { from: `/clientes/${clienteId}` } })
                        }
                      >
                        <FiExternalLink size={13} aria-hidden="true" /> Abrir lead
                      </button>
                    </div>
                    <div className="tiny muted" style={{ marginTop: 10 }}>
                      Valor estimado{" "}
                      <span className="num" style={{ color: "var(--ink-2)", fontWeight: 600 }}>
                        {lead.valor_estimado != null
                          ? Number(lead.valor_estimado).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                          : "—"}
                      </span>{" "}
                      · criado em {fmtDataLonga(lead.created_at)}
                      {lead.timeline?.length || lead.tarefas?.length || lead.anexos?.length
                        ? " · tudo preservado (abra o lead para tarefas, compromissos e anexos)"
                        : ""}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          ) : null}
        </div>

        {/* DIREITA */}
        <div className="stack" style={{ gap: 16 }}>
          <Panel
            title="Notas internas"
            icon={<FiFileText aria-hidden="true" />}
            action={
              podeEditar ? (
                <button type="button" className="btn btn-ghost btn-sm" onClick={onNovaNota}>
                  <FiPlus size={13} aria-hidden="true" /> Nova
                </button>
              ) : null
            }
          >
            <div className="panel-body stack" style={{ gap: 10 }}>
              {notas.length === 0 ? (
                <div className="muted sm">
                  Sem notas internas. Registre contexto (preferências, decisor, histórico).
                </div>
              ) : (
                notas.map((n) => (
                  <NotaItem key={n.id} nota={n} podeExcluir={podeEditar} onExcluir={() => onExcluirNota(n.id)} />
                ))
              )}
            </div>
          </Panel>

          {spinLead ? (
            <Panel title="Diagnóstico SPIN" icon={<FiInbox aria-hidden="true" />}>
              <div className="panel-body stack" style={{ gap: 13 }}>
                {SPIN_CATS.map((c) => {
                  const itens = spinLead.spin[c.key] || [];
                  if (!itens.length) return null;
                  return (
                    <div key={c.key}>
                      <div className="eyebrow" style={{ color: "var(--accent)" }}>
                        {c.label}
                      </div>
                      {itens.map((n, i) => (
                        <div key={i} style={{ fontSize: 13, marginTop: 3, lineHeight: 1.5 }}>
                          {n.texto}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </Panel>
          ) : null}

          <Panel title="Resumo de contato" icon={<FiUsers aria-hidden="true" />}>
            <div className="panel-body grid-2" style={{ gap: 12 }}>
              {[
                { label: "WhatsApp", v: resumo.whatsapp, color: "#25D366" },
                { label: "E-mails", v: resumo.email, color: "var(--info-solid)" },
                { label: "Ligações", v: resumo.ligacao, color: "var(--accent)" },
                { label: "Reuniões", v: resumo.reuniao, color: "var(--warning-solid)" },
              ].map((r) => (
                <div className="flex center" style={{ gap: 9 }} key={r.label}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: r.color, flexShrink: 0 }} />
                  <div>
                    <div className="num" style={{ fontWeight: 600, fontSize: 16 }}>
                      {r.v}
                    </div>
                    <div className="tiny muted">{r.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </section>
  );
}

export default HistoricoTab;
